import React, { useState, RefObject } from 'react'
import { Box, Button, IconButton, Menu, MenuItem } from '@mui/material'
import MoveDownRoundedIcon from '@mui/icons-material/MoveDownRounded'
import ArrowCircleUpRoundedIcon from '@mui/icons-material/ArrowCircleUpRounded'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../store/hooks'
import IconTooltip from '../icon-tooltip/icon-tooltip'

interface SectionItem {
  id: string
  title: {
    help: string | null
    text: {
      de: string
      en: string
    }
  }
}

interface SectionNavigatorDropdownProps {
  sections: SectionItem[]
  scrollContainerRef: RefObject<HTMLDivElement | null>
}

const SectionNavigatorDropdown: React.FC<SectionNavigatorDropdownProps> = ({
  sections,
  scrollContainerRef,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)
  const { currentLanguage } = useAppSelector((state) => state.currentUser)
  const [activeId, setActiveId] = useState<string | null>(null)
  const { t } = useTranslation()

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = (id: string) => {
    const section = document.getElementById(id)
    if (section && scrollContainerRef.current) {
      // setActiveId(section.id)
      const offset = 0
      const containerTop = scrollContainerRef.current.getBoundingClientRect().top
      const sectionTop = section.getBoundingClientRect().top
      const scrollOffset = sectionTop - containerTop + scrollContainerRef.current.scrollTop - offset

      scrollContainerRef.current.scrollTo({
        top: scrollOffset,
        behavior: 'smooth',
      })
    }

    handleClose()
  }

  return (
    <>
      <Box sx={{ mr: '-1.12rem' }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleButtonClick}
          aria-controls={menuOpen ? 'section-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? 'true' : undefined}
        >
          <IconTooltip title={t('Jump to section')} placement="left" arrow>
            <MoveDownRoundedIcon fontSize="medium" />
          </IconTooltip>
        </Button>
      </Box>

      <Box sx={{ mr: '-1.12rem' }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => handleSelect(sections[0].id)}
          aria-controls="back-to-top"
        >
          <IconTooltip title={t('Back to top')} placement="left" arrow>
            <ArrowCircleUpRoundedIcon fontSize="medium" />
          </IconTooltip>
        </Button>
      </Box>

      <Menu
        id="section-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        MenuListProps={{ sx: { maxHeight: 500, maxWidth: 450 } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {sections.map((section) => (
          <MenuItem
            key={section.id}
            onClick={() => handleSelect(section.id)}
            sx={{
              whiteSpace: 'normal',
              borderBottom: '1px solid',
              borderColor: 'action.disabledBackground',
              fontWeight: activeId === section.id ? 'bold' : 'normal',
              backgroundColor: activeId === section.id ? 'secondary.light' : '',
            }}
          >
            {section.title.text[currentLanguage]}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default SectionNavigatorDropdown
